import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, ChevronDown } from 'lucide-react';
import { useI18n } from '../i18n';

const SERVICE_ITEMS = [
  { key: 'cardio', to: '/valoracion-cardiometabolica' },
  { key: 'adiposity', to: '/valoracion-adiposidad-metabolismo' },
  { key: 'diabetes', to: '/valoracion-diabetes-tipo-2' },
  { key: 'senior', to: '/senior-saludable' },
  { key: 'rehab', to: '/rehabilitacion-funcional' },
  { key: 'family', to: '/medicina-de-familia' },
];

const Header = () => {
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!servicesOpen) return undefined;
    const handler = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [servicesOpen]);

  const isOnService = SERVICE_ITEMS.some((s) => location.pathname === s.to);

  return (
    <header className="glass-nav fixed top-0 left-0 right-0 z-50" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex flex-col" data-testid="brand-link">
            <span className="font-brand text-xl tracking-[0.32em] text-cyan-vidat">VIDAT</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mt-0.5">{t('brand.tagline')}</span>
          </Link>

          <nav className="hidden xl:flex items-center gap-8">
            <NavLink
              to="/"
              end
              data-testid="nav-home"
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors ${isActive ? 'text-cyan-vidat font-medium' : 'text-stone-700 hover:text-cyan-vidat'}`
              }
            >
              {t('nav.home')}
            </NavLink>

            <div
              className="relative"
              ref={servicesRef}
            >
              <button
                onClick={() => setServicesOpen((s) => !s)}
                className={`flex items-center gap-1.5 text-[13px] tracking-wide transition-colors ${
                  isOnService || servicesOpen ? 'text-cyan-vidat font-medium' : 'text-stone-700 hover:text-cyan-vidat'
                }`}
                data-testid="nav-services-toggle"
                aria-expanded={servicesOpen}
              >
                {t('nav.services')}
                <ChevronDown size={14} strokeWidth={1.5} className={`transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div
                  className="absolute left-0 top-full mt-3 z-50"
                  data-testid="nav-services-dropdown"
                >
                  <div className="bg-white border border-stone-200 shadow-lg py-3 min-w-[340px]">
                    {SERVICE_ITEMS.map((item) => (
                      <NavLink
                        key={item.key}
                        to={item.to}
                        onClick={() => setServicesOpen(false)}
                        data-testid={`nav-service-${item.key}`}
                        className={({ isActive }) =>
                          `block px-6 py-2.5 text-sm leading-snug transition-colors ${
                            isActive ? 'text-cyan-vidat bg-ivory font-medium' : 'text-stone-700 hover:text-cyan-vidat hover:bg-ivory'
                          }`
                        }
                      >
                        {t(`nav.${item.key}`)}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink
              to="/sobre-vidat"
              data-testid="nav-about"
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors ${isActive ? 'text-cyan-vidat font-medium' : 'text-stone-700 hover:text-cyan-vidat'}`
              }
            >
              {t('nav.about')}
            </NavLink>
          </nav>

          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => setLangOpen((s) => !s)}
                className="flex items-center gap-1.5 text-[12px] tracking-widest uppercase text-stone-700 hover:text-cyan-vidat transition-colors"
                data-testid="lang-switcher"
              >
                <Globe size={14} strokeWidth={1.5} />
                {lang.toUpperCase()}
              </button>
              {langOpen && (
                <div
                  className="absolute right-0 mt-3 bg-white border border-stone-200 shadow-sm py-2 min-w-[120px]"
                  onMouseLeave={() => setLangOpen(false)}
                >
                  {['es', 'ca'].map((l) => (
                    <button
                      key={l}
                      onClick={() => { setLang(l); setLangOpen(false); }}
                      data-testid={`lang-option-${l}`}
                      className={`block w-full text-left px-4 py-1.5 text-[12px] tracking-wider uppercase ${
                        l === lang ? 'text-cyan-vidat font-medium' : 'text-stone-600 hover:text-cyan-vidat'
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="hidden md:block">
              <Link to="/programar-visita" className="btn-primary" data-testid="header-cta">
                {t('nav.cta')}
              </Link>
            </div>

            <button
              className="xl:hidden ml-1 text-stone-700"
              onClick={() => setOpen((s) => !s)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-stone-200 max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-1">
            <NavLink
              to="/"
              end
              data-testid="mobile-nav-home"
              className={({ isActive }) =>
                `py-3 text-[15px] tracking-wide ${isActive ? 'text-cyan-vidat font-medium' : 'text-stone-700'}`
              }
            >
              {t('nav.home')}
            </NavLink>

            <div className="py-2">
              <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mb-2 mt-2">
                {t('nav.services')}
              </div>
              <div className="flex flex-col gap-1">
                {SERVICE_ITEMS.map((item) => (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    data-testid={`mobile-nav-service-${item.key}`}
                    className={({ isActive }) =>
                      `py-2 pl-3 text-[14px] border-l ${
                        isActive ? 'text-cyan-vidat font-medium border-cyan-vidat' : 'text-stone-700 border-stone-200'
                      }`
                    }
                  >
                    {t(`nav.${item.key}`)}
                  </NavLink>
                ))}
              </div>
            </div>

            <NavLink
              to="/sobre-vidat"
              data-testid="mobile-nav-about"
              className={({ isActive }) =>
                `py-3 text-[15px] tracking-wide mt-2 ${isActive ? 'text-cyan-vidat font-medium' : 'text-stone-700'}`
              }
            >
              {t('nav.about')}
            </NavLink>

            <Link
              to="/programar-visita"
              className="btn-primary mt-5 self-start"
              data-testid="mobile-cta"
            >
              {t('nav.cta')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
